/**********************************************************************
// @@@ START COPYRIGHT @@@
//        AVI SOLAR ENERGY CONFIDENTIAL: NEED TO KNOW ONLY
//        Copyright 2018 - 2019
//        Avi Solar Energy Pvt Ltd.
//        Protected as an unpublished work.
//  The computer program listings, specifications and documentation
//  herein are the property of Avi Solar Energy Pvt Ltd., or a third party supplier
//  and shall not be reproduced, copied, disclosed, or used in whole or in part for
//  any reason without the prior express written permission of Avi Solar Energy Pvt Ltd.
// @@@ END COPYRIGHT @@@
// +++ Code modified on 2019/02/25
**********************************************************************/
import color from '@/util/color-generator.js'
import { mapGetters, mapMutations } from 'vuex'
import _ from 'lodash'
import {
  EventBus
} from '@/services/event-bus'

const moment = require('moment')
const momentTz = require('moment-timezone')

export const mixin = {
  data() {
    return {
      defaultStartDate: new Date('2017-01-01'),
      defaultEndDate: new Date()
    }
  },
  methods: {
    dateValidate(date) {
      if (date < this.defaultStartDate || date > this.defaultEndDate) {
        return false
      }
      return true
    },
    simpleString(string) {
      // return string.replace(/ /g, '').replace(/[^0-9][&\/\\#,+()$~%.'":*-?<>{}]/g, '')
      return string.replace(/ /g, '').replace(/[^a-zA-Z0-9 ]/g, '')
    }
  },
  computed: {
    isMobileView() {
      return this.$vuetify.breakpoint.name === 'sm' || this.$vuetify.breakpoint.name === 'xs'
    }
  },
  filters: {
    // Round the value to n decimal places where n is an input parameter
    round(value, n = 2) {
      if (value === 'N/A' || value === null || value === undefined) {
        return 'N/A'
      }
      return Number.parseFloat(value).toFixed(n)
    },
    numberWithCommas: (x) => {
      return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    },
    value: (x) => {
      if (x > 99999) { return (x / 1000).toFixed(3) } else { return Math.round(x) }
    },
    unit: (x, component) => {
      if (component == 'dc') {
        if (x > 99999) { return 'MWp' } else { return 'kWp' }
      }
      if (component == 'ac') {
        if (x > 99999) { return 'MW' } else { return 'kW' }
      }
      if (component == 'totalGen') {
        if (x > 99999) { return 'MWh' } else { return 'kWh' }
      }
      if (component == 'availPercent') {
        if (x == 100.00) { return '100' } else if (x == 0.00) { return '0' } else { return x }
      }
    },
    truncateText: (text) => {
      let length = window.innerHeight < 600 ? 7 : 15
      if (text.length > length) {
        return text.substring(0, length) + '...'
      }
      return text
    },
    truncateText10: (text) => {
      let length = 10
      if (text.length > length) {
        return text.substring(0, length) + '...'
      }
      return text
    }
  }
}

export const filterMixin = {
  data() {
    return {
      modifiedDashboardName: '',
      activeParam: {}
    }
  },
  computed: {
    ...mapGetters(['getGeneralState', 'getFilterOptions', 'getSelectedPlantId', 'getActivePlantInfo', 'getDefaultDate']),
    filterOption() {
      let dashboardName = this.$route.name + this.modifiedDashboardName
      return _.cloneDeep(this.getFilterOptions[dashboardName])
    },
    staticFilterConfig() {
      let Obj = {}
      this.filterOption.forEach(el => {
        el.children.forEach(child => {
          if ((child.condition && (!!child.considerStateAlways || child.condition(Obj[child.ref]))) || !child.condition) {
            if (child.type === 'select') {
              if (typeof child.value === 'function') {
                Obj[child.id] = child.value(Obj[child.ref])
              } else {
                Obj[child.id] = child.value[0]
              }
            } else if (child.type === 'datePicker' || child.type === 'singleDatePicker') {
              let parameters = ['from', 'to']
              parameters.forEach(key => {
                if (!Obj[child.id]) { Obj[child.id] = {} }
                Obj[child.id][key] = this.getDefaultDate
              })
            } else {
              Obj[child.id] = child.value
            }
          }
        })
      })
      return this.filterOption[0].type === 'static' ? Obj : {}
    }
  },
  mounted() {
    EventBus.$on('filter_disruptor', this.filterDisruptor)
    this.RESET_ACTIVE_FILTER()
    this.SET_ACTIVE_FILTER(this.staticFilterConfig)
  },
  destroyed() {
    this.SET_DEFAULT_DATE(momentTz().tz(this.getActivePlantInfo('timezone')).format('YYYY-MM-DD'))
    this.RESET_ACTIVE_FILTER()
    EventBus.$off('filter_disruptor', this.filterDisruptor)
  },
  methods: {
    ...mapMutations(['SET_DEFAULT_DATE', 'SET_ACTIVE_FILTER', 'RESET_ACTIVE_FILTER']),
    filterDisruptor(val) {
      this.modifiedDashboardName = val.name
    },
    setDynamicFilterConfig(parameters) {
      this.filterOption.forEach(el => {
        el.children.forEach((child, index) => {
          this.$set(this.activeParam, child.id, parameters[index])
        })
      })

      this.RESET_ACTIVE_FILTER()
      this.SET_ACTIVE_FILTER(this.activeParam)
    }
  },
  watch: {
    staticFilterConfig: {
      handler(val) {
        if (_.isEmpty(val)) { return }

        this.RESET_ACTIVE_FILTER()
        this.SET_ACTIVE_FILTER(val)
      },
      immediate: true
    },
    getSelectedPlantId() {
      this.RESET_ACTIVE_FILTER()
      this.SET_ACTIVE_FILTER(this.staticFilterConfig)
    }
  }
}

export const mapGettersMixin = {
  data() {
    return {
      cacheInterval: null
    }
  },
  beforeDestroy() {
    if (this.cacheInterval) {
      clearInterval(this.cacheInterval)
    }
  },
  computed: {
    ...mapGetters(['getGeneralState']),
    isDark() {
      return this.getGeneralState('theme_dark')
    },
    theme() {
      return this.isDark ? 'dark' : 'light'
    },
    isReload() {
      return this.getGeneralState('autoReload') && (this.getGeneralState('autoReload') || this.getGeneralState('instantRefresh'))
    },
    reloadTime() {
      return this.getGeneralState('reloadTimer')
    }
  },
  methods: {
    setIntervalWithoutDelay(func, interval = this.reloadTime) {
      if (this.cacheInterval) {
        clearInterval(this.cacheInterval)
      }
      func()
      this.cacheInterval = setInterval(func, interval)
      return this.cacheInterval
    }
  }
}

export const colorMixin = {
  data() {
    return {
      dataLength: 0
    }
  },
  computed: {
    colors() {
      return color.generateColor(this.dataLength)
    }
  }
}

export const miscellaneous = {
  methods: {
    isObject(param) {
      return param != null && typeof param === 'object'
    },
    // TODO: Move this to Mixin (COMPARE OBJECT)
    isObjectEqual(object1, object2) {
      const keys1 = Object.keys(object1)
      const keys2 = Object.keys(object2)
      if (keys1.length !== keys2.length) {
        return false
      }
      for (const key of keys1) {
        const val1 = object1[key]
        const val2 = object2[key]
        const areObjects = this.isObject(val1) && this.isObject(val2)
        if (
          (areObjects && !this.isObjectEqual(val1, val2)) ||
          (!areObjects && val1 !== val2)
        ) {
          return false
        }
      }
      return true
    },
    arrDifference(arr1, arr2) {
      return _.difference(arr1, arr2)
    },
    isArrEqual(a, b) {
      return a.length === b.length &&
      a.every((v, i) => {
        if (Array.isArray(v)) {
          return this.isArrEqual(v, b[i])
        } else if (typeof v === 'string') {
          return v === b[i]
        } else if (typeof v === 'object') {
          return this.isObjectEqual(v, b[i])
        }
      })
    },
    arraySortHandler(dataArray, param, order) {
      return dataArray.sort((a, b) => {
        let clonedA = _.cloneDeep(a)
        let clonedB = _.cloneDeep(b)
        if(clonedA['date_frame'] == 'Total' || clonedB['date_frame'] == 'Total') {
          return 0
        }
        if (isNaN(+clonedA[param]) && typeof clonedA[param] !== 'undefined') {
          if (/-[0-9]+/g.test(clonedB[param])) {
            if (/-[0-9]+/g.test(clonedA[param])) {
              if (clonedA[param].replace(/[0-9.]/g, '') !== clonedB[param].replace(/[0-9.]/g, '')) {
                return order === 'asc' ? -1 : 1
              } else {
                if (order === 'asc') {
                  return +(clonedA[param].replace(/[^0-9.]/g, '')) - +(clonedB[param].replace(/[^0-9.]/g, ''))
                } else {
                  return +(clonedB[param].replace(/[^0-9.]/g, '')) - +(clonedA[param].replace(/[^0-9.]/g, ''))
                }
              }
            } else {
              return order === 'asc' ? -1 : 1
            }
          } else if (/-[0-9]+/g.test(clonedA[param])) {
            return order === 'asc' ? 1 : -1
          } else {
            return 0
          }
        } else {
          if (typeof clonedA[param] === 'undefined' || clonedA[param] === null || clonedA[param] === 'N/A' || clonedA[param] === 'No Data') {
            clonedA[param] = 0
          }
          if (typeof clonedB[param] === 'undefined' || clonedB[param] === null || clonedB[param] === 'N/A' || clonedB[param] === 'No Data') {
            clonedB[param] = 0
          }
          if (clonedA[param] < clonedB[param]) {
            return order === 'asc' ? -1 : 1
          } else if (clonedA[param] > clonedB[param]) {
            return order === 'asc' ? 1 : -1
          } else {
            return 0
          }
        }
      })
    }
  }
}

export const timezoneMixin = {
  data() {
    return {
      time: momentTz().format('YYYY-MM-DD HH:mm z')
    }
  },
  computed: {
    ...mapGetters(['getActivePlantInfo'])
  },
  methods: {
    updateTime() {
      this.time = this.timezoneTime()
    },
    timezoneTime(t) {
      return momentTz(t).tz(this.getActivePlantInfo('timezone')).format('YYYY-MM-DD HH:mm z')
    }
  }
}

export const unitConvertorMixin = {
  methods: {
    convert(value, unit, finalUnit) {
      const table = ['w', 'k', 'm', 'g', 't']

      if (value >= 1000) {
        const currentUnit = (unit.split('')[0]).toLowerCase()
        const currentIndex = table.indexOf(currentUnit)
        const requiredUnit = (finalUnit.split('')[0]).toLowerCase()
        const requiredIndex = table.indexOf(requiredUnit)
        const unitDifference = requiredIndex - currentIndex
        return `${(value / Math.pow(1000, unitDifference)).toFixed(2)} ${finalUnit}`
      } else {
        return `${value.toFixed(2)} ${unit}`
      }
    },
    minuteTOHours(value, unit, finalUnit) {
      const table = ['s', 'm', 'h']
      if (value >= 60) {
        const currentUnit = (unit.split('')[0]).toLowerCase()
        const currentIndex = table.indexOf(currentUnit)
        const requiredUnit = (finalUnit.split('')[0]).toLowerCase()
        const requiredIndex = table.indexOf(requiredUnit)
        const unitDifference = requiredIndex - currentIndex
        const hours = Math.floor((value / Math.pow(60, unitDifference)))
        return `${hours} ${finalUnit} ${hours * Math.pow(60, unitDifference) === value ? '' : value - (hours * Math.pow(60, unitDifference))} ${unit}`
      } else {
        return `${value} ${unit}`
      }
    }
  }
}
